import React, { useState, useEffect } from 'react';
import './App.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import AuthPage from './components/AuthPage';
import Dashboard from './components/Dashboard';
import UploadForm from './components/UploadForm';
import MouListGmail from './components/MouListGmail';
import MouViewer from './components/MouViewer';
import EditMou from './components/EditMou';
import api from './api';
import { buildPermissions } from './utils/roleUtils';
import NotificationsDropdown from './components/NotificationsDropdown';
import ChatbotWidget from './components/ChatbotWidget';

function AppContent() {
  const { user, logout, isAuthenticated, loading: authLoading } = useAuth();
  const [mous, setMous] = useState([]);
  const [selectedMou, setSelectedMou] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [viewerInitialTab, setViewerInitialTab] = useState(null);

  useEffect(() => {
    if (user) {
      fetchUserPermissions();
    } else {
      setPermissions(null);
      setSelectedMou(null);
      setActiveTab('dashboard');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (user && permissions) {
      setActiveTab(permissions.can.viewDashboard ? 'dashboard' : 'list');
    }
  }, [permissions, user]);

  const fetchUserPermissions = async () => {
    try {
      const response = await api.get('/api/auth/user', {
        params: { email: user.email }
      });
      const { role, spocMous = [] } = response.data;
      setPermissions(buildPermissions(role, spocMous));
    } catch (err) {
      console.error('Error fetching user permissions:', err);
    }
  };

  useEffect(() => {
    let timer;
    const fetchUnread = async () => {
      try {
        if (!user?.email) return;
        const { data } = await api.get('/api/notifications', { params: { email: user.email } });
        setUnreadCount(data.unreadCount || 0);
      } catch (e) {
        // silent fail
      }
    };
    if (user) {
      fetchUnread();
      timer = setInterval(fetchUnread, 60000);
    }
    return () => timer && clearInterval(timer);
  }, [user]);

  const refreshUnread = async () => {
    try {
      if (!user?.email) return;
      const { data } = await api.get('/api/notifications', { params: { email: user.email } });
      setUnreadCount(data.unreadCount || 0);
    } catch {}
  };

  useEffect(() => {
    if (!user?.email) return;
    const url = `${api.defaults.baseURL}/api/notifications/stream?email=${encodeURIComponent(user.email)}`;
    const es = new EventSource(url);
    es.onmessage = (ev) => {
      try {
        const payload = JSON.parse(ev.data);
        if (payload.type === 'init' && typeof payload.unreadCount === 'number') {
          setUnreadCount(payload.unreadCount);
        } else if (payload.type === 'new') {
          setUnreadCount((c) => c + 1);
          if (showNotifications) {
            refreshUnread();
          }
        }
      } catch {}
    };
    es.onerror = () => {
      es.close();
    };
    return () => es.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email, showNotifications]);

  useEffect(() => {
    if (activeTab === 'list' || activeTab === 'view') {
      fetchMous();
    }
  }, [activeTab]);

  const fetchMous = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/api/mous');
      setMous(response.data);
    } catch (err) {
      setError('Failed to fetch MoUs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSuccess = () => {
    setActiveTab('list');
    fetchMous();
  };

  const handleViewMou = async (prefix, tab = null) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/api/mou/${prefix}`);
      setSelectedMou(response.data);
      if (tab) setViewerInitialTab(tab);
      setActiveTab('view');
    } catch (err) {
      setError('Failed to fetch MoU details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEdit = async (prefix) => {
    if (!permissions?.can.editMou(prefix)) return;

    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/api/mou/${prefix}`);
      setSelectedMou(response.data);
      setActiveTab('edit');
    } catch (err) {
      setError('Failed to load MoU for editing');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSaved = async () => {
    if (!selectedMou) return;
    await handleViewMou(selectedMou.prefix);
  };

  const handleDeleteMou = async (prefix) => {
    if (!permissions?.can.deleteMou(prefix)) return;

    if (!window.confirm(`Are you sure you want to delete MoU: ${prefix}?`)) {
      return;
    }

    try {
      await api.delete(`/api/mou/${prefix}`);
      fetchMous();
      if (selectedMou && selectedMou.prefix === prefix) {
        setSelectedMou(null);
        setActiveTab('list');
      }
    } catch (err) {
      setError('Failed to delete MoU');
      console.error(err);
    }
  };

  if (authLoading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AuthPage />;
  }

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-content">
          <h1>📋 MoU Management System</h1>
          <div className="header-nav">
            {permissions?.can.viewDashboard && (
              <button
                className={`nav-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
                onClick={() => setActiveTab('dashboard')}
              >
                📊 Dashboard
              </button>
            )}
            <button
              className={`nav-btn ${activeTab === 'list' ? 'active' : ''}`}
              onClick={() => setActiveTab('list')}
            >
              📑 MoUs
            </button>
            {permissions?.can.uploadMou && (
              <button
                className={`nav-btn ${activeTab === 'upload' ? 'active' : ''}`}
                onClick={() => setActiveTab('upload')}
              >
                ⬆️ Upload
              </button>
            )}
          </div>
          <div className="header-right">
            <div className="notif-wrapper">
              <button
                className="notif-bell"
                onClick={() => setShowNotifications(s => !s)}
                title="Notifications"
              >
                🔔
                {unreadCount > 0 && <span className="notif-badge">{unreadCount}</span>}
              </button>
              {showNotifications && (
                <NotificationsDropdown
                  userEmail={user.email}
                  onClose={() => setShowNotifications(false)}
                  onMarkedRead={refreshUnread}
                  onNavigateToMou={(prefix, type) => {
                    const tab = type === 'comment' ? 'community' : 'activities';
                    setViewerInitialTab(tab);
                    handleViewMou(prefix);
                  }}
                />)
              }

            </div>
            <span className="user-email">{user.email}</span>
            <span className="user-role-badge">{permissions?.role}</span>
            <button className="btn-logout" onClick={logout}>
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="main-content">
        {error && (
          <div className="error-message">{error}</div>
        )}

        {activeTab === 'dashboard' && permissions?.can.viewDashboard && (
          <Dashboard permissions={permissions} onViewMou={handleViewMou} />
        )}

        {activeTab === 'upload' && permissions?.can.uploadMou && (
          <UploadForm onSuccess={handleUploadSuccess} userEmail={user?.email} />
        )}

        {activeTab === 'list' && (
          loading ? (
            <div className="loading">Loading MoUs...</div>
          ) : (
            <MouListGmail
              mous={mous}
              permissions={permissions}
              onView={handleViewMou}
              onEdit={handleOpenEdit}
              onDelete={handleDeleteMou}
              userEmail={user?.email}
            />
          )
        )}

        {activeTab === 'view' && selectedMou && (
          <MouViewer
            mou={selectedMou}
            onBack={() => setActiveTab('list')}
            onEdit={
              permissions?.can.editMou(selectedMou.prefix)
                ? () => handleOpenEdit(selectedMou.prefix)
                : null
            }
            userEmail={user?.email}
            permissions={permissions}
            initialTab={viewerInitialTab}
          />
        )}

        {activeTab === 'edit' && selectedMou && permissions?.can.editMou(selectedMou.prefix) && (
          <EditMou
            mou={selectedMou}
            onCancel={() => setActiveTab('view')}
            onSaved={handleEditSaved}
          />
        )}
      </main>

      <ChatbotWidget />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
